import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  SectionHeader,
} from "@babackapp/customer-ui/components/header";
import { Label } from "@babackapp/customer-ui/components/label";
import {
  Option,
  OptionAction,
  OptionDescription,
  OptionTitle,
  OptionsList,
} from "@babackapp/customer-ui/components/option";
import {
  RadioGroup,
  RadioGroupItem,
} from "@babackapp/customer-ui/components/radio-group";
import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import type { ShippingRateDto } from "@babackapp/private-api-client/generated/requests/types.gen";
import { EnterIcon } from "@radix-ui/react-icons";
import { Separator } from "@babackapp/ui/components/separator";
import { useCheckout } from "../hooks/use-checkout";

export function Checkout() {
  const {
    financialSummary,
    shippingRates,
    selectedRateId,
    selectRate,
    canProceed,
    onProceed,
    isLoadingRates,
  } = useCheckout();

  const needsPayment = financialSummary.amountDue > 0;

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>Paiement</HeaderTitle>
        <HeaderDescription>
          {needsPayment
            ? "Pour confirmer votre retour, veuillez payer les frais supplémentaires."
            : "Vérifiez le récapitulatif de votre retour avant de confirmer."}
        </HeaderDescription>
      </Header>

      <div className="w-full mt-4 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          {financialSummary.refundTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>Total à rembourser</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(
                  financialSummary.refundTotal,
                  financialSummary.currency,
                )}
              </ReceiptLineValue>
            </ReceiptLine>
          )}

          {financialSummary.exchangeTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>Total échange</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(
                  financialSummary.exchangeTotal,
                  financialSummary.currency,
                )}
              </ReceiptLineValue>
            </ReceiptLine>
          )}

          {financialSummary.discountTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>Bon de réduction</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(
                  financialSummary.discountTotal,
                  financialSummary.currency,
                )}
              </ReceiptLineValue>
            </ReceiptLine>
          )}

          {needsPayment && (
            <>
              <Separator className="my-1" />
              <ReceiptLine>
                <ReceiptLineLabel className="font-medium text-foreground">
                  Montant dû
                </ReceiptLineLabel>
                <ReceiptLineValue className="font-medium text-foreground">
                  {formatPrice(
                    financialSummary.amountDue,
                    financialSummary.currency,
                  )}
                </ReceiptLineValue>
              </ReceiptLine>
            </>
          )}
        </div>

        {needsPayment && (
          <div className="flex flex-col gap-3">
            <SectionHeader>Choisissez un mode de livraison</SectionHeader>

            {isLoadingRates && (
              <p className="text-sm text-muted-foreground">
                Chargement des options de livraison…
              </p>
            )}

            {!isLoadingRates && shippingRates.length > 0 && (
              <OptionsList>
                <RadioGroup
                  value={selectedRateId ?? ""}
                  onValueChange={selectRate}
                >
                  {shippingRates.map((rate) => (
                    <ShippingRateOption
                      key={rate.handle}
                      rate={rate}
                      currency={financialSummary.currency}
                    />
                  ))}
                </RadioGroup>
              </OptionsList>
            )}
          </div>
        )}
      </div>

      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer
          layoutId="line-item-confirm-button"
          variants={{}}
        >
          <Button disabled={!canProceed} onClick={onProceed}>
            <EnterIcon />
            Confirmer
          </Button>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}

type ShippingRateOptionProps = {
  rate: ShippingRateDto;
  currency: string;
};

function ShippingRateOption({ rate, currency }: ShippingRateOptionProps) {
  const priceAmount = parseFloat(rate.price.amount);
  const priceCents = Math.round(priceAmount * 100);
  const rateCurrency = rate.price.currencyCode ?? currency;

  return (
    <Label htmlFor={`shipping-rate-${rate.handle}`}>
      <Option>
        <OptionTitle>{rate.title}</OptionTitle>
        <OptionDescription>
          {formatPrice(priceCents, rateCurrency)}
        </OptionDescription>
        <OptionAction>
          <RadioGroupItem
            value={rate.handle}
            id={`shipping-rate-${rate.handle}`}
          />
        </OptionAction>
      </Option>
    </Label>
  );
}
